import { useDisclosure } from '@cardboard-ui/react';
import React, { FC } from 'react';
import { OptionsMenuItem } from 'components/OptionsMenu/OptionsMenuItem';
import { t } from '@lingui/macro';
import { graphql, useFragment, useMutation } from 'utils/graphClient';
import { ThumbnailOption_node$key } from './__generated__/ThumbnailOption_node.graphql';
import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ImageUploadModal from 'components/ImageUploadModal/ImageUploadModal';
import { ThumbnailOption_setThumbnailMutation } from './__generated__/ThumbnailOption_setThumbnailMutation.graphql';

const nodeFragment = graphql`
  fragment ThumbnailOption_node on Node {
    id
    ... on Folder {
      canUpdateThumbnail: canRename {
        value
      }
    }
    ... on Vault {
      canUpdateThumbnail: canUpdate {
        value
      }
    }
    ... on HasThumbnailInterface {
      __typename
      thumbnail {
        url
      }
    }
  }
`;

const setThumbnailMutation = graphql`
  mutation ThumbnailOption_setThumbnailMutation(
    $nodeId: ID!
    $thumbnail: ThumbnailInput
  ) {
    setThumbnail(input: { nodeId: $nodeId, thumbnail: $thumbnail }) {
      node {
        ... on Folder {
          canUpdateThumbnail: canRename {
            value
          }
        }
        ... on Vault {
          canUpdateThumbnail: canUpdate {
            value
          }
        }
        ... on HasThumbnailInterface {
          __typename
          thumbnail {
            url
          }
        }
      }
    }
  }
`;

interface ThumbnailOptionProps {
  node: ThumbnailOption_node$key;
}

export const ThumbnailOption = ({ node }: ThumbnailOptionProps) => {
  const _node = useFragment(nodeFragment, node);

  if (_node.__typename === 'Folder' && _node.canUpdateThumbnail?.value) {
    return <InnerThumbnailOption node={node} />;
  }

  if (_node.__typename === 'Vault' && _node.canUpdateThumbnail?.value) {
    return <InnerThumbnailOption node={node} />;
  }

  return null;
};

const InnerThumbnailOption = ({ node }: ThumbnailOptionProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  return (
    <>
      <OptionsMenuItem
        icon={<FontAwesomeIcon icon={light('rectangle-code')} />}
        onClick={onOpen}
      >{t`Thumbnail`}</OptionsMenuItem>
      {isOpen && <ThumbnailDialog node={node} onClose={onClose} />}
    </>
  );
};

interface ThumbnailDialogProps extends ThumbnailOptionProps {
  onClose: () => void;
}

const ThumbnailDialog: FC<ThumbnailDialogProps> = ({ node, onClose }) => {
  const { id, thumbnail, __typename } = useFragment(nodeFragment, node);
  const cropVariant = __typename === 'Vault' ? 'circle' : 'none';

  const [updateThumbnail, isThumbnailUpdating] =
    useMutation<ThumbnailOption_setThumbnailMutation>(setThumbnailMutation);
  return (
    <ImageUploadModal
      isOpen={true}
      onClose={onClose}
      cropVariant={cropVariant}
      headingText={t`Set thumbnail`}
      imageURL={thumbnail?.url || ''}
      onImageSelected={({ newImage, onComplete }) => {
        updateThumbnail({
          variables: {
            nodeId: id,
            thumbnail: newImage,
          },
          onCompleted: onComplete,
        });
      }}
    />
  );
};
